/**
 * GameConfig — every tuning constant from ECHO_DODGE_FULL_GAME_SPEC.md §25.
 * This file is the single source of truth for the web twin. The Swift
 * GameConfig.swift mirrors it 1:1 and is validated by shared test vectors.
 *
 * Any change that affects scores, collision, geometry, delays, speed, shard
 * placement, combo, or timer MUST increment `configVersion` (spec §25).
 */

export const ENGINE_VERSION = 5; // v5: shield unlimited, stasis 5s, radial shockwave, echo brake (Worlds §W10)
export const CONFIG_VERSION = 4;

/** Authoritative mechanics step (spec §19.1). */
export const STEP = 1 / 120;
export const STEPS_PER_SECOND = 120;
/** History sampling: every 2nd mechanics step = 60 Hz (spec §5.4). */
export const HISTORY_HZ = 60;

export const CONFIG = {
  arenaW: 360,
  arenaH: 560,
  // Playable center bounds (spec §4.1)
  boundsMinX: 20,
  boundsMaxX: 340,
  boundsMinY: 48,
  boundsMaxY: 525,
  playerStartX: 180,
  playerStartY: 325,

  playerVisualRadius: 10.0,
  playerCollisionRadius: 7.5,
  echoVisualRadius: 11.5,
  echoCollisionRadius: 9.0,
  /** player + echo collision radii (spec §4.5). Compare with `<`, never `<=` (spec §23.1). */
  collisionThreshold: 16.5,

  /** units/second (spec §4.2) */
  touchSpeed: 515,
  debugKeySpeed: 205,
  fingerOffsetDefault: 44,

  /**
   * Ring buffer retention. v3: 1024 samples ≈ 17 s — stasis lags echo playback
   * behind real time, so retention must cover maxDelay + trail + total frozen
   * time (levels author at most two stasis pickups; validated in gen-levels).
   */
  historyCapacity: 1024,
  trailDuration: 0.54,
  trailSpacing: 0.045,
  trailPoints: 13,

  lives: 3,
  invulnSteps: 126, // 1.05 s × 120
  hitFreezeSeconds: 0.075, // real time, does not consume sim steps (spec §4.5)
  spawnFadeSteps: 36, // 0.30 s × 120 — visible but non-collidable (spec §5.3)
  roundSteps: 3600, // 30.0 s × 120

  comboWindowSteps: 252, // 2.10 s × 120, inclusive (spec §6.1/§23.1)
  comboMax: 8,
  baseShardPoints: 20,
  completionBonusPerLife: 100,

  pickupDistance: 22,
  pickupDistanceSq: 484,

  // Shard placement (spec §6.4)
  shardCandidates: 24,
  shardBoundsMinX: 34,
  shardBoundsMaxX: 326,
  shardBoundsMinY: 56,
  shardBoundsMaxY: 510,
  shardMinPlayerDist: 105,
  shardMinEchoDist: 54,
  shardMinPrevDist: 90,
  routeCrossWindow: 0.35,
  routeCrossDist: 20,
  /**
   * Spec gaps, pinned here so both engines agree (docs/DESIGN_DECISIONS.md):
   * echo-distance contribution to candidateScore is capped at 400 units, and
   * with no previous shard the distance-from-previous term uses 200 units.
   */
  echoDistCap: 400,
  noPrevShardDist: 200,

  // Walls (Worlds §W1) — fixed push-out passes keep resolution deterministic.
  wallPushIterations: 2,
  /** Shard candidates must sit at least this far outside every wall surface. */
  shardWallClearance: 26,
  /**
   * Gates only: shards keep extra clearance from a gate's FULL TRAVEL BAND so
   * a moving slab never parks on top of a shard (Worlds §W4).
   */
  shardGateClearance: 34,

  // Entities (Worlds §W5)
  mineTelegraphSteps: 120, // 1.0 s ring before the mine arms
  mineRadius: 12,
  entityPickupDistance: 22,
  entityPickupDistanceSq: 484,
  timeLeakSteps: 360, // −3.0 s stolen from the clock
  shieldSteps: 960, // 8.0 s window — absorbs every hit inside it (v5)
  stasisSteps: 600, // 5.0 s of frozen echo playback (v5)

  // Reflex telemetry (Worlds §W6) — pressure = a collidable echo within range.
  reflexPressureRange: 90,
  reflexPressureRangeSq: 8100,

  // Late-game entities + loadout (Worlds §W8)
  glitchSteps: 240, // 2.0 s of inverted drag
  surgeSteps: 120, // echoes surge 1.0 s closer to the present
  shockPushDistance: 150, // radial blast: echoes shoved this far from the spark (v5)
  shockDecaySteps: 300, // 2.5 s glide back onto their timelines
  brakeSteps: 720, // ECHO BRAKE: 6.0 s of half-speed echo playback (v5)

  telegraphLead: 2.2,
  finalWarningSeconds: 6.0,
  /** boundary_dancer: within 30 units of an edge (spec §11.1) */
  edgeZone: 30,
  edgeStepsRequired: 1200, // 10 s × 120
  firstEchoSurviveSteps: 600, // first_echo: 5 s × 120
} as const;

export type EchoShape = "circle" | "diamond" | "split" | "hex";

export interface EchoDef {
  id: string;
  unlockStep: number;
  delay: number; // seconds — playback offset into player history
  /** F "MIRROR" (Worlds §W8): replay reflected across the vertical axis. */
  mirror?: boolean;
  shape: EchoShape;
  notches: number;
  collisionThreshold: number;
}

/** Classic Run echo schedule (spec §5.2). The delay order is intentional. */
export const CLASSIC_ECHOES: readonly EchoDef[] = [
  { id: "A", unlockStep: 288, delay: 2.4, shape: "circle", notches: 1, collisionThreshold: 16.5 }, // 2.40 s
  { id: "B", unlockStep: 1080, delay: 4.6, shape: "diamond", notches: 2, collisionThreshold: 16.5 }, // 9.00 s
  { id: "C", unlockStep: 1920, delay: 1.35, shape: "split", notches: 3, collisionThreshold: 16.5 }, // 16.00 s
  { id: "D", unlockStep: 2760, delay: 6.2, shape: "hex", notches: 4, collisionThreshold: 16.5 }, // 23.00 s
];

/**
 * Campaign-only echoes (Worlds §W8). E "BLINK" replays you from 0.8 s ago —
 * nothing exists to replay before 0.8 s, so it bites almost immediately.
 * F "MIRROR" replays your route reflected across the arena's vertical axis:
 * your past hunts you from the other side of the world.
 */
export const LEVEL_ECHO_TABLE: readonly EchoDef[] = [
  ...CLASSIC_ECHOES,
  { id: "E", unlockStep: 1200, delay: 0.8, shape: "circle", notches: 5, collisionThreshold: 16.5 },
  { id: "F", unlockStep: 1800, delay: 3.1, shape: "diamond", notches: 6, collisionThreshold: 16.5, mirror: true },
];

/**
 * Unranked onboarding preset for the first three Classic runs (spec §7.1):
 * first echo unlocks at 4.0 s, Echo A collision radius reduced by 15 %
 * (16.5 − 9.0×0.15 = 15.15), only A, B, C activate.
 */
export function trainingEchoes(): EchoDef[] {
  return [
    { id: "A", unlockStep: 480, delay: 2.4, shape: "circle", notches: 1, collisionThreshold: 15.15 },
    { id: "B", unlockStep: 1080, delay: 4.6, shape: "diamond", notches: 2, collisionThreshold: 16.5 },
    { id: "C", unlockStep: 1920, delay: 1.35, shape: "split", notches: 3, collisionThreshold: 16.5 },
  ];
}

/** Practice Lab: every enabled echo unlocks at 2.4 s (spec §7.2). */
export function practiceEchoes(enabled: { A: boolean; B: boolean; C: boolean; D: boolean }): EchoDef[] {
  return CLASSIC_ECHOES.filter((d) => enabled[d.id as "A" | "B" | "C" | "D"]).map((d) => ({
    ...d,
    unlockStep: 288,
  }));
}
